import React, { useState, useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * ExportCodeDialog - Dialog for displaying and copying exported deck code
 * shadcn/ui version
 */
export default function ExportCodeDialogShadcn({
  open,
  onOpenChange,
  exportCode,
  cards = [],
}) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('code');

  const deckJson = useMemo(() => {
    if (!cards || cards.length === 0) return '{}';
    const deckData = {
      version: 2,
      cards: cards.map(card => ({
        number: card.cardNumber || card.number,
        count: card.count || 1,
      })),
    };
    return JSON.stringify(deckData, null, 2);
  }, [cards]);

  const currentContent = activeTab === 'code' ? exportCode : deckJson;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setCopied(false);
      setActiveTab('code');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('controls.exportDeckCode', 'Export Deck Code')}
          </DialogTitle>
          <DialogDescription>
            {t('deck.export.instructions', 'Copy this code to share your deck or import it later.')}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); setCopied(false); }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="code">
              {t('deck.export.codeTab', 'Deck Code')}
            </TabsTrigger>
            <TabsTrigger value="json">
              {t('deck.export.jsonTab', 'JSON')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="code" className="mt-3 space-y-3">
            <textarea
              className="w-full min-h-[100px] rounded-md border border-input bg-muted/50 px-3 py-2 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
              value={exportCode}
              readOnly
            />
          </TabsContent>
          <TabsContent value="json" className="mt-3 space-y-3">
            <textarea
              className="w-full min-h-[200px] rounded-md border border-input bg-muted/50 px-3 py-2 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y"
              value={deckJson}
              readOnly
            />
          </TabsContent>
        </Tabs>
        <div className="space-y-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={handleCopy} className="w-full">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t('common.copied', 'Copied!')}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    {activeTab === 'code'
                      ? t('deck.export.copyCode', 'Copy Code')
                      : t('deck.export.copyJson', 'Copy JSON')}
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {copied ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
            </TooltipContent>
          </Tooltip>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
